import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2,
  Users,
  CheckCircle,
  Loader2,
  AlertCircle,
  Search,
  ChevronDown,
  ChevronUp,
  User,
  Briefcase,
  Shield,
  Mail,
  Calendar,
  HardDrive,
  Terminal,
  Copy,
  ExternalLink,
  XCircle,
  Lock,
  MessageSquare,
} from 'lucide-react';
import connectionService, {
  type ConnectionResult,
  type AccessTestResult,
  type DirectoryUser,
  type SubsystemResult,
  type CapabilityName,
} from '../services/connectionService';

type FlowMode = 'choose' | 'planA' | 'planB';
type PlanAStep = 'instructions' | 'connecting' | 'results';
type PlanBStep = 'consent' | 'selectMailbox' | 'testing' | 'results';

const AzureSetup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [flowMode, setFlowMode] = useState<FlowMode>('choose');
  const [planAStep, setPlanAStep] = useState<PlanAStep>('instructions');
  const [planBStep, setPlanBStep] = useState<PlanBStep>('consent');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResults, setTestResults] = useState<AccessTestResult | null>(null);
  const [connectionResult, setConnectionResult] = useState<ConnectionResult | null>(null);

  // Plan B state
  const [directoryUsers, setDirectoryUsers] = useState<DirectoryUser[]>([]);
  const [selectedTestUser, setSelectedTestUser] = useState<DirectoryUser | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [azureTenantId, setAzureTenantId] = useState('');
  const [showPowerShell, setShowPowerShell] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);

  // Check if returning from callback
  useEffect(() => {
    const callbackResult = searchParams.get('connectionResult');
    const callbackError = searchParams.get('connectionError');
    const returnMode = searchParams.get('mode');

    if (callbackResult) {
      try {
        const result = JSON.parse(decodeURIComponent(callbackResult));
        setConnectionResult(result);
        setTestResults(result.testResults);
        setFlowMode('planA');
        setPlanAStep('results');
      } catch {
        setError('Failed to parse callback result');
      }
      navigate('/azure-setup', { replace: true });
    } else if (callbackError) {
      setError(decodeURIComponent(callbackError));
      setFlowMode('planA');
      setPlanAStep('results');
      navigate('/azure-setup', { replace: true });
    } else if (returnMode === 'planB') {
      const tenantId = searchParams.get('tenant');
      if (tenantId) {
        setAzureTenantId(tenantId);
        setFlowMode('planB');
        setPlanBStep('selectMailbox');
        // Stage 1: register pending connection, then load directory
        registerPendingAndLoadDirectory(tenantId);
      }
      navigate('/azure-setup', { replace: true });
    }
  }, [searchParams, navigate]);

  // ─── Plan A handlers ──────────────────────────────────────────

  const handleStartPlanA = async () => {
    setLoading(true);
    setError('');
    try {
      // Request only Phase 1 capabilities (directory + mail)
      const { authUrl } = await connectionService.startDelegatedConnection(['directory', 'mail']);
      window.location.href = authUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to start connection');
      setLoading(false);
    }
  };

  // ─── Plan B handlers ──────────────────────────────────────────

  const handleAdminConsent = () => {
    const clientId = '0e2011bd-b31e-47fa-bf9b-9a8ff7d5ec9c';
    const redirectUri = 'https://platform.niwt.com/callback';
    const consentUrl = `https://login.microsoftonline.com/common/adminconsent?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=planB`;
    window.location.href = consentUrl;
  };

  const loadDirectoryUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await connectionService.getDirectoryUsers({ pageSize: 100 });
      setDirectoryUsers(result.users);
    } catch (err: any) {
      setError(err.message || 'Failed to load directory users');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Plan B Stage 1: register a pending connection so directory-sync can list users,
   * then load the directory.
   */
  const registerPendingAndLoadDirectory = async (tenantId: string) => {
    setLoading(true);
    setError('');
    try {
      await connectionService.registerAppOnlyConnection(tenantId);
      const result = await connectionService.getDirectoryUsers({ pageSize: 100 });
      setDirectoryUsers(result.users);
    } catch (err: any) {
      setError(err.message || 'Failed to register connection or load directory');
    } finally {
      setLoading(false);
    }
  };

  const handleTestPlanB = async () => {
    if (!selectedTestUser || !azureTenantId) return;

    setLoading(true);
    setError('');
    setPlanBStep('testing');

    try {
      const result = await connectionService.registerAppOnlyConnection(
        azureTenantId,
        selectedTestUser.id
      );
      setConnectionResult(result);
      setTestResults(result.testResults);
      setPlanBStep('results');
    } catch (err: any) {
      setError(err.message || 'Access test failed');
      setPlanBStep('results');
    } finally {
      setLoading(false);
    }
  };

  // ─── Shared handlers ──────────────────────────────────────────

  const handleProceed = () => {
    navigate('/chat');
  };

  const handleRetest = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await connectionService.testConnection();
      setConnectionResult(result);
      setTestResults(result.testResults);
    } catch (err: any) {
      setError(err.message || 'Re-test failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCommand(true);
    setTimeout(() => setCopiedCommand(false), 2000);
  };

  const filteredUsers = directoryUsers.filter(u => {
    const q = searchQuery.toLowerCase();
    return (
      u.displayName?.toLowerCase().includes(q) ||
      u.mail?.toLowerCase().includes(q) ||
      u.jobTitle?.toLowerCase().includes(q) ||
      u.department?.toLowerCase().includes(q)
    );
  });

  // ─── Mode selection screen ────────────────────────────────────

  if (flowMode === 'choose') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 p-3">
              <img src="/images/niwt-logo.png" alt="Niwt" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 font-libre">Connect Microsoft 365</h1>
            <p className="mt-2 text-gray-600">Choose how to connect your organization</p>
          </div>

          <div className="space-y-4">
            {/* Plan A — Recommended */}
            <button
              onClick={() => { setFlowMode('planA'); setPlanAStep('instructions'); }}
              className="w-full bg-white rounded-2xl shadow-lg border-2 border-transparent hover:border-niwt-primary p-6 text-left transition-all group"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-niwt-primary to-purple-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-bold text-gray-900">Connect Microsoft 365</h2>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Recommended</span>
                  </div>
                  <p className="text-gray-600 mt-1">
                    Sign in with a dedicated service account (e.g., niwt@yourcompany.com).
                    Most reliable for SMB tenants.
                  </p>
                  <ul className="mt-3 space-y-1 text-sm text-gray-500">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Quick setup — just sign in</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Works with all M365 license types</span>
                    </li>
                  </ul>
                </div>
              </div>
            </button>

            {/* Plan B — Advanced */}
            <button
              onClick={() => { setFlowMode('planB'); setPlanBStep('consent'); }}
              className="w-full bg-white rounded-2xl shadow-lg border-2 border-transparent hover:border-gray-300 p-6 text-left transition-all"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">Advanced: App-Only Access</h2>
                  <p className="text-gray-600 mt-1">
                    Grant application-level consent without a service account.
                    No extra license needed, but may require additional configuration.
                  </p>
                  <ul className="mt-3 space-y-1 text-sm text-gray-500">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-gray-400" />
                      <span>No service account or license required</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <AlertCircle className="w-4 h-4 text-amber-400" />
                      <span>May need Exchange Application Access Policy</span>
                    </li>
                  </ul>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Plan A flow ──────────────────────────────────────────────

  if (flowMode === 'planA') {
    if (planAStep === 'instructions') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center px-4">
          <div className="max-w-lg w-full">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 p-3">
                <img src="/images/niwt-logo.png" alt="Niwt" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 font-libre">Service Account Setup</h1>
              <p className="mt-2 text-gray-600">Create a dedicated account for Niwt to access your data</p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
              {error && <ErrorBanner message={error} />}

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-3">Before you begin:</h3>
                <ol className="space-y-2 text-sm text-blue-800 list-decimal list-inside">
                  <li>Create a service account (e.g., <strong>niwt@yourcompany.com</strong>)</li>
                  <li>Assign a <strong>minimum license</strong> with Exchange Online (e.g., Microsoft 365 Business Basic)</li>
                  <li>Wait ~15 minutes for the mailbox to provision</li>
                </ol>
              </div>

              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <strong>License required:</strong> The service account must have a license that includes
                    Exchange Online. Without it, Niwt cannot read email or calendar data.
                  </p>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-2">Niwt will request access to:</h3>
                <div className="space-y-1 text-sm text-purple-800">
                  <PermissionItem icon={Users} text="Read directory and user info (User.Read.All)" />
                  <PermissionItem icon={Mail} text="Read email messages (Mail.Read)" />
                </div>
              </div>

              <button
                onClick={handleStartPlanA}
                disabled={loading}
                className="w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-niwt-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Preparing...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Continue to Microsoft Sign-In
                  </>
                )}
              </button>

              <button
                onClick={() => setFlowMode('choose')}
                className="w-full text-gray-500 text-sm hover:text-gray-700 transition-colors"
              >
                Back to connection options
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (planAStep === 'connecting') {
      return <LoadingScreen message="Connecting to Microsoft 365..." />;
    }

    if (planAStep === 'results') {
      return (
        <TestResultsScreen
          testResults={testResults}
          connectionResult={connectionResult}
          error={error}
          loading={loading}
          onProceed={handleProceed}
          onRetest={handleRetest}
          onBack={() => { setPlanAStep('instructions'); setError(''); setTestResults(null); }}
        />
      );
    }
  }

  // ─── Plan B flow ──────────────────────────────────────────────

  if (flowMode === 'planB') {
    if (planBStep === 'consent') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center px-4">
          <div className="max-w-lg w-full">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 p-3">
                <img src="/images/niwt-logo.png" alt="Niwt" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 font-libre">App-Only Access</h1>
              <p className="mt-2 text-gray-600">Grant Niwt application-level access to your tenant</p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
              {error && <ErrorBanner message={error} />}

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">What happens next:</h3>
                <ol className="space-y-1 text-sm text-blue-800 list-decimal list-inside">
                  <li>You'll be redirected to Microsoft's admin consent page</li>
                  <li>Sign in with your organization admin account</li>
                  <li>Approve application permissions for Niwt</li>
                  <li>Return here to select a test mailbox and verify access</li>
                </ol>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <h3 className="font-semibold text-gray-900 mb-2">Niwt will initially verify:</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <PermissionItem icon={Users} text="Read directory and user info (User.Read.All)" />
                  <PermissionItem icon={Mail} text="Read email messages (Mail.Read)" />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Calendar and file access can be unlocked later from your settings.
                </p>
              </div>

              <button
                onClick={handleAdminConsent}
                className="w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-niwt-primary/90 transition-colors flex items-center justify-center"
              >
                <Shield className="w-5 h-5 mr-2" />
                Grant Admin Consent
              </button>

              <button
                onClick={() => setFlowMode('choose')}
                className="w-full text-gray-500 text-sm hover:text-gray-700 transition-colors"
              >
                Back to connection options
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (planBStep === 'selectMailbox') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-niwt-primary to-purple-700 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 font-libre">Select Test Mailbox</h1>
                    <p className="text-gray-600">Choose a user to verify Niwt can access email and calendar</p>
                  </div>
                </div>
              </div>

              {error && <ErrorBanner message={error} />}

              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4">
                <p className="text-sm text-blue-800">
                  Select a licensed user with an active mailbox. Niwt will read one email and one calendar
                  event (metadata only) to verify access works.
                </p>
              </div>

              <div className="flex items-center space-x-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent"
                  />
                </div>
                <button
                  onClick={handleTestPlanB}
                  disabled={!selectedTestUser || loading}
                  className="px-6 py-2 bg-niwt-primary text-white rounded-lg hover:bg-niwt-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Test Access
                </button>
              </div>

              {loading && !directoryUsers.length ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-niwt-primary animate-spin mr-2" />
                  <span className="text-gray-600">Loading directory users...</span>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => setSelectedTestUser(u)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center space-x-3 ${
                        selectedTestUser?.id === u.id
                          ? 'border-niwt-primary bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-niwt-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900 truncate">{u.displayName}</span>
                          {u.licensed && (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Licensed</span>
                          )}
                          {!u.mailEnabled && (
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">No mailbox</span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500 truncate block">{u.mail || u.userPrincipalName}</span>
                      </div>
                      {u.jobTitle && (
                        <span className="text-xs text-gray-400 hidden md:block">{u.jobTitle}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* PowerShell commands for Exchange Application Access Policy */}
              <div className="mt-6 border-t border-gray-200 pt-4">
                <button
                  onClick={() => setShowPowerShell(!showPowerShell)}
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Terminal className="w-4 h-4" />
                  <span>Security hardening: Enforce mailbox allowlist (recommended)</span>
                  {showPowerShell ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showPowerShell && (
                  <div className="mt-3 bg-gray-900 rounded-lg p-4 text-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 text-xs">PowerShell — Exchange Online</span>
                      <button
                        onClick={() => copyToClipboard(getPowerShellCommands(azureTenantId))}
                        className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        <span className="text-xs">{copiedCommand ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <pre className="text-green-400 whitespace-pre-wrap text-xs leading-relaxed">
                      {getPowerShellCommands(azureTenantId)}
                    </pre>
                    <p className="text-gray-500 text-xs mt-3">
                      This restricts Niwt to only access mailboxes in the specified group.
                      To remove: <code className="text-amber-400">Remove-ApplicationAccessPolicy -Identity "niwt-{azureTenantId?.slice(0, 8)}"</code>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (planBStep === 'testing') {
      return <LoadingScreen message="Running access test..." />;
    }

    if (planBStep === 'results') {
      return (
        <TestResultsScreen
          testResults={testResults}
          connectionResult={connectionResult}
          error={error}
          loading={loading}
          onProceed={handleProceed}
          onRetest={handleRetest}
          onBack={() => { setPlanBStep('selectMailbox'); setError(''); setTestResults(null); }}
        />
      );
    }
  }

  return null;
};

// ─── Shared components ──────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
      <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
      <span className="text-sm">{message}</span>
    </div>
  );
}

function PermissionItem({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div className="flex items-center space-x-2">
      <Icon className="w-4 h-4 text-niwt-primary" />
      <span>{text}</span>
    </div>
  );
}

function LoadingScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <Loader2 className="w-12 h-12 text-niwt-primary animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 font-libre mb-2">{message}</h2>
          <p className="text-gray-600 text-sm">This may take a few moments...</p>
        </div>
      </div>
    </div>
  );
}

function TestResultsScreen({
  testResults,
  connectionResult,
  error,
  loading,
  onProceed,
  onRetest,
  onBack,
}: {
  testResults: AccessTestResult | null;
  connectionResult: ConnectionResult | null;
  error: string;
  loading: boolean;
  onProceed: () => void;
  onRetest: () => void;
  onBack: () => void;
}) {
  const [unlockingCap, setUnlockingCap] = useState<CapabilityName | null>(null);
  const [unlockError, setUnlockError] = useState('');
  const [unlockedResults, setUnlockedResults] = useState<Partial<Record<CapabilityName, SubsystemResult>>>({});

  // Phase 1 passed when directory + mail are ok
  const phase1Passed = testResults
    ? testResults.directory?.status === 'ok' && testResults.mail?.status === 'ok'
    : false;

  const handleUnlock = async (capability: CapabilityName) => {
    setUnlockingCap(capability);
    setUnlockError('');
    try {
      const result = await connectionService.unlockCapability(capability);
      if (result.action === 'consent_required' && result.authUrl) {
        // Plan A: redirect to Microsoft for incremental consent
        sessionStorage.setItem('niwt_oauth_state', result.state || '');
        window.location.href = result.authUrl;
      } else if (result.action === 'tested' && result.result) {
        // Plan B: test ran immediately, show inline result
        setUnlockedResults(prev => ({ ...prev, [capability]: result.result! }));
      }
    } catch (err: any) {
      setUnlockError(err.message || `Failed to unlock ${capability}`);
    } finally {
      setUnlockingCap(null);
    }
  };

  const PHASE_2_CAPS: { capability: CapabilityName; label: string; icon: any }[] = [
    { capability: 'calendar', label: 'Calendar', icon: Calendar },
    { capability: 'sharepoint', label: 'SharePoint / OneDrive', icon: HardDrive },
    { capability: 'teams', label: 'Teams', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          {phase1Passed ? (
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          ) : (
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          )}
          <h1 className="text-3xl font-bold text-gray-900 font-libre">
            {phase1Passed ? 'Connection Successful' : 'Connection Issues Detected'}
          </h1>
          {connectionResult?.serviceAccountUpn && (
            <p className="mt-2 text-gray-600">
              Service account: {connectionResult.serviceAccountUpn}
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-4">
          {error && <ErrorBanner message={error} />}
          {unlockError && <ErrorBanner message={unlockError} />}

          {testResults && (
            <div className="space-y-3">
              {/* Phase 1 capabilities — actively tested */}
              <SubsystemRow label="Directory" icon={Users} result={testResults.directory} />
              <SubsystemRow label="Mail" icon={Mail} result={testResults.mail} />

              {/* Phase 2+ capabilities — unlockable when Phase 1 passes */}
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                  {phase1Passed ? 'Additional Capabilities' : 'Available After Connection'}
                </p>
                {PHASE_2_CAPS.map(({ capability, label, icon }) => {
                  const unlocked = unlockedResults[capability];
                  if (unlocked) {
                    return <SubsystemRow key={capability} label={label} icon={icon} result={unlocked} />;
                  }
                  return (
                    <LockedCapabilityRow
                      key={capability}
                      label={label}
                      icon={icon}
                      onUnlock={phase1Passed ? () => handleUnlock(capability) : undefined}
                      unlocking={unlockingCap === capability}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-4 space-y-3">
            {phase1Passed ? (
              <button
                onClick={onProceed}
                className="w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-niwt-primary/90 transition-colors"
              >
                Continue Setup
              </button>
            ) : (
              <button
                onClick={onRetest}
                disabled={loading}
                className="w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-niwt-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Re-testing...
                  </>
                ) : (
                  'Re-test Connection'
                )}
              </button>
            )}
            <button
              onClick={onBack}
              className="w-full text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubsystemRow({
  label,
  icon: Icon,
  result,
}: {
  label: string;
  icon: any;
  result: SubsystemResult;
}) {
  if (result.status === 'not_tested') {
    return <LockedCapabilityRow label={label} icon={Icon} />;
  }

  const passed = result.status === 'ok';
  return (
    <div className={`p-3 rounded-lg border ${passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon className={`w-5 h-5 ${passed ? 'text-green-600' : 'text-red-600'}`} />
          <span className={`font-medium ${passed ? 'text-green-900' : 'text-red-900'}`}>{label}</span>
        </div>
        {passed ? (
          <CheckCircle className="w-5 h-5 text-green-600" />
        ) : (
          <XCircle className="w-5 h-5 text-red-600" />
        )}
      </div>
      {!passed && result.remediationHint && (
        <p className="text-xs text-red-700 mt-1 ml-7">{result.remediationHint}</p>
      )}
      {passed && result.userCount !== undefined && (
        <p className="text-xs text-green-700 mt-1 ml-7">{result.userCount} users found</p>
      )}
    </div>
  );
}

function LockedCapabilityRow({
  label,
  icon: Icon,
  onUnlock,
  unlocking = false,
}: {
  label: string;
  icon: any;
  onUnlock?: () => void;
  unlocking?: boolean;
}) {
  if (onUnlock) {
    return (
      <button
        onClick={onUnlock}
        disabled={unlocking}
        className="w-full p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-purple-50 hover:border-niwt-primary/30 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-600">{label}</span>
          </div>
          {unlocking ? (
            <Loader2 className="w-4 h-4 text-niwt-primary animate-spin" />
          ) : (
            <span className="text-xs text-niwt-primary font-medium flex items-center space-x-1">
              <Lock className="w-3 h-3" />
              <span>Unlock</span>
            </span>
          )}
        </div>
      </button>
    );
  }

  return (
    <div className="p-3 rounded-lg border border-gray-100 bg-gray-50 opacity-60">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon className="w-5 h-5 text-gray-400" />
          <span className="font-medium text-gray-400">{label}</span>
        </div>
        <Lock className="w-4 h-4 text-gray-300" />
      </div>
    </div>
  );
}

function getPowerShellCommands(tenantId: string): string {
  const policyName = `niwt-${tenantId?.slice(0, 8) || 'tenant'}`;
  return `# Connect to Exchange Online
Connect-ExchangeOnline

# Create a mail-enabled security group for Niwt access
New-DistributionGroup -Name "Niwt Access" -Type Security

# Add mailboxes Niwt should access
Add-DistributionGroupMember -Identity "Niwt Access" -Member "user@yourcompany.com"

# Create Application Access Policy
New-ApplicationAccessPolicy \\
  -AppId "0e2011bd-b31e-47fa-bf9b-9a8ff7d5ec9c" \\
  -PolicyScopeGroupId "Niwt Access" \\
  -AccessRight RestrictAccess \\
  -Description "${policyName}"

# Verify the policy
Test-ApplicationAccessPolicy \\
  -AppId "0e2011bd-b31e-47fa-bf9b-9a8ff7d5ec9c" \\
  -Identity "user@yourcompany.com"`;
}

export default AzureSetup;
